// Right-hand pane of the split view: header plus the four tabs. Owns every
// per-workflow mutation and dialog that used to live on BrandedQRCard.
// QR lives on the row (WorkflowQrModal), not here.

import { useCallback, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  MdContentCopy,
  MdDelete,
  MdDownload,
  MdEdit,
  MdLanguage,
  MdMoreVert,
  MdPlayArrow,
  MdStorage,
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { GoChecklist } from 'react-icons/go';
import { SiReacthookform } from 'react-icons/si';
import { useNavigate } from 'react-router-dom';
import {
  useChatbotSettings,
  useChatbotStatus,
  useDeleteChatbot,
  useDeleteFlow,
  usePreviewFlow,
  usePublishFlow,
  useSyncFlow,
} from '@/lib/sdk/chatbot/queries';
import { Chatbot, UpdateSettingsDto } from '@/lib/sdk/chatbot/types';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { Modal } from '@/design-system/components/Modal';
import { EditMethodDialog } from '@/components/Chatbot/EditMethodDialog';
import { getFlowState, deepLink, startCommand } from './flowState';
import { OverviewTab } from './tabs/OverviewTab';
import { SettingsTab } from './tabs/SettingsTab';
import { FlowTab } from './tabs/FlowTab';
import { TeamApprovalsTab } from './tabs/TeamApprovalsTab';

interface WorkflowDetailPaneProps {
  chatbot: Chatbot;
  /** Called after a delete succeeds so the page can drop the selection. */
  onDeleted: () => void;
}

export function WorkflowDetailPane({ chatbot, onDeleted }: WorkflowDetailPaneProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [previewJson, setPreviewJson] = useState<string | null>(null);

  const deleteDialog = useDisclosure();
  const statusDialog = useDisclosure();
  const deleteFlowDialog = useDisclosure();
  const previewDialog = useDisclosure();
  const editMethodDialog = useDisclosure();

  const settingsMutation = useChatbotSettings();
  const statusMutation = useChatbotStatus();
  const deleteMutation = useDeleteChatbot();
  const syncFlowMutation = useSyncFlow();
  const publishFlowMutation = usePublishFlow();
  const deleteFlowMutation = useDeleteFlow();
  const previewFlowMutation = usePreviewFlow();

  const flowState = getFlowState(chatbot);
  const isChecklist = chatbot.bot_type === 'static';

  const showToast = useCallback(
    (title: string, status: 'success' | 'error' = 'success') => {
      toast({ title, status, duration: 3000 });
    },
    [toast]
  );

  const copyToClipboard = useCallback(
    (value: string, message: string) => {
      navigator.clipboard.writeText(value);
      showToast(message);
    },
    [showToast]
  );

  const handleSaveSettings = useCallback(
    async (settings: UpdateSettingsDto) => {
      try {
        await settingsMutation.mutateAsync({ id: chatbot.short_code, data: settings });
        showToast('Settings updated successfully');
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Failed to update settings', 'error');
      }
    },
    [chatbot.short_code, settingsMutation, showToast]
  );

  // Confirmed rather than immediate: this takes effect on live traffic the moment it
  // lands — a deactivated workflow stops answering WhatsApp requests straight away.
  const handleToggleStatus = useCallback(async () => {
    try {
      await statusMutation.mutateAsync({ id: chatbot.id, data: { status: !chatbot.blocked } });
      showToast(chatbot.blocked ? 'Workflow activated' : 'Workflow deactivated');
      statusDialog.onClose();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to update status', 'error');
    }
  }, [chatbot.id, chatbot.blocked, statusMutation, showToast, statusDialog]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteMutation.mutateAsync(chatbot.id);
      showToast('Workflow deleted');
      deleteDialog.onClose();
      onDeleted();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to delete workflow', 'error');
    }
  }, [chatbot.id, deleteMutation, showToast, deleteDialog, onDeleted]);

  const handlePreviewFlow = useCallback(async () => {
    try {
      const result = await previewFlowMutation.mutateAsync(chatbot.id);
      setPreviewJson(JSON.stringify(result.flow_json, null, 2));
      previewDialog.onOpen();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to generate preview', 'error');
    }
  }, [chatbot.id, previewFlowMutation, showToast, previewDialog]);

  const handleSyncFlow = useCallback(async () => {
    try {
      await syncFlowMutation.mutateAsync(chatbot.id);
      showToast('Flow synced successfully');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to sync flow', 'error');
    }
  }, [chatbot.id, syncFlowMutation, showToast]);

  const handlePublishFlow = useCallback(async () => {
    try {
      await publishFlowMutation.mutateAsync(chatbot.id);
      showToast('Flow published successfully');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to publish flow', 'error');
    }
  }, [chatbot.id, publishFlowMutation, showToast]);

  const handleDeleteFlow = useCallback(async () => {
    try {
      await deleteFlowMutation.mutateAsync(chatbot.id);
      showToast(
        chatbot.flow_published ? 'Flow deprecated successfully' : 'Flow deleted successfully'
      );
      deleteFlowDialog.onClose();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to delete flow', 'error');
    }
  }, [chatbot.id, chatbot.flow_published, deleteFlowMutation, showToast, deleteFlowDialog]);

  return (
    <VStack align="stretch" spacing={0} h="100%">
      {/* Header. pr leaves room for the panel's close button, which floats top-right. */}
      <Box pl={6} pr={14} py={5} borderBottomWidth="1px" borderColor="border-default">
        <HStack justify="space-between" align="flex-start" spacing={4}>
          <VStack align="flex-start" spacing={2} minW={0}>
            <HStack spacing={3}>
              <Text
                data-testid="wfl-panel-title"
                fontSize="xl"
                fontWeight="bold"
                color="text-primary"
                noOfLines={1}
              >
                {chatbot.title}
              </Text>
              <Badge colorScheme={chatbot.blocked ? 'red' : 'green'}>
                {chatbot.blocked ? 'Inactive' : 'Active'}
              </Badge>
            </HStack>
            <HStack spacing={3}>
              <Tooltip label="Copy command" placement="top">
                <Box
                  as="button"
                  type="button"
                  fontFamily="mono"
                  fontSize="sm"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  bg="bg-muted"
                  borderWidth="1px"
                  borderColor="border-muted"
                  color="text-secondary"
                  _hover={{ borderColor: 'brand.300', color: 'brand.500' }}
                  onClick={() =>
                    copyToClipboard(startCommand(chatbot.short_code), 'Command copied to clipboard!')
                  }
                >
                  {startCommand(chatbot.short_code)}
                </Box>
              </Tooltip>
              <HStack spacing={1.5}>
                <Icon
                  as={isChecklist ? GoChecklist : SiReacthookform}
                  boxSize={3.5}
                  color="text-secondary"
                />
                <Text fontSize="sm" color="text-primary">
                  {isChecklist ? 'Checklist' : 'Form'}
                </Text>
              </HStack>
              {chatbot.department_label && (
                <Badge variant="outline" colorScheme="gray" fontWeight="normal" textTransform="none">
                  {chatbot.department_label}
                </Badge>
              )}
              {/* Channels live here rather than in the table — they change rarely and
                  are only interesting once you are already looking at one workflow. */}
              <HStack spacing={2}>
                <Tooltip label="WhatsApp" placement="top">
                  <span><Icon as={FaWhatsapp} boxSize={4} color="green.500" /></span>
                </Tooltip>
                <Tooltip
                  label={
                    chatbot.web_channel_enabled ? 'Web channel enabled' : 'Web channel disabled'
                  }
                  placement="top"
                >
                  <span>
                    <Icon
                      as={MdLanguage}
                      boxSize={4}
                      color={chatbot.web_channel_enabled ? 'brand.500' : 'border-default'}
                    />
                  </span>
                </Tooltip>
              </HStack>
            </HStack>
          </VStack>

          <VStack align="flex-end" spacing={3} flexShrink={0}>
            <FormControl display="flex" alignItems="center" justifyContent="flex-end" gap={2} w="auto">
              <FormLabel htmlFor="workflow-active" mb={0} fontSize="sm" color="text-secondary">
                Active
              </FormLabel>
              <Switch
                data-testid="wfl-panel-active-switch"
                id="workflow-active"
                colorScheme="brand"
                isChecked={!chatbot.blocked}
                onChange={statusDialog.onOpen}
                isDisabled={statusMutation.isPending}
              />
            </FormControl>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<MdPlayArrow />}
                data-testid="wfl-panel-start-btn"
                isDisabled={chatbot.blocked || !chatbot.web_channel_enabled}
                onClick={() =>
                  navigate(`/dashboard/${chatbot.org_id}/workflows/${chatbot.short_code}/execute`)
                }
              >
                Start
              </Button>
              <Button
                size="sm"
                colorScheme="brand"
                leftIcon={<MdEdit />}
                data-testid="wfl-panel-update-tasks-btn"
                onClick={editMethodDialog.onOpen}
              >
                Update tasks
              </Button>
              <Menu placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  size="sm"
                  variant="ghost"
                  icon={<MdMoreVert />}
                  aria-label="More actions"
                  data-testid="wfl-panel-menu-btn"
                />
                <MenuList>
                  <MenuItem
                    icon={<MdStorage />}
                    onClick={() =>
                      navigate(`/dashboard/${chatbot.org_id}/workflows/${chatbot.id}/data`)
                    }
                  >
                    Edit data
                  </MenuItem>
                  <MenuItem
                    icon={<MdContentCopy />}
                    onClick={() =>
                      copyToClipboard(deepLink(chatbot), 'Copied to clipboard!')
                    }
                  >
                    Copy WhatsApp link
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem
                    icon={<MdDelete />}
                    color="status-error"
                    onClick={deleteDialog.onOpen}
                    data-testid="wfl-panel-delete-btn"
                  >
                    Delete workflow
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </VStack>
        </HStack>
      </Box>

      {/* Tabs */}
      <Tabs colorScheme="brand" flex={1} display="flex" flexDirection="column" overflow="hidden" isLazy>
        <TabList px={6}>
          <Tab data-testid="wfl-panel-tab-overview">Overview</Tab>
          <Tab data-testid="wfl-panel-tab-settings">Settings</Tab>
          <Tab data-testid="wfl-panel-tab-flow">
            WhatsApp Flow
            {flowState && (
              <Badge ml={2} colorScheme={flowState.colorScheme} fontSize="2xs">
                {flowState.label}
              </Badge>
            )}
          </Tab>
          <Tab data-testid="wfl-panel-tab-team">Team &amp; approvals</Tab>
        </TabList>

        <TabPanels flex={1} overflowY="auto">
          <TabPanel px={6} py={5}>
            <OverviewTab chatbot={chatbot} />
          </TabPanel>
          <TabPanel px={6} py={5}>
            <SettingsTab
              chatbot={chatbot}
              isSaving={settingsMutation.isPending}
              onSave={handleSaveSettings}
            />
          </TabPanel>
          <TabPanel px={6} py={5}>
            <FlowTab
              chatbot={chatbot}
              isPreviewing={previewFlowMutation.isPending}
              isSyncing={syncFlowMutation.isPending}
              isPublishing={publishFlowMutation.isPending}
              isDeleting={deleteFlowMutation.isPending}
              onPreview={handlePreviewFlow}
              onSync={handleSyncFlow}
              onPublish={handlePublishFlow}
              onDelete={deleteFlowDialog.onOpen}
            />
          </TabPanel>
          <TabPanel px={6} py={5}>
            <TeamApprovalsTab
              chatbot={chatbot}
              onManageTeam={() =>
                navigate(
                  `/dashboard/${chatbot.org_id}/team?chatbot_id=${chatbot.id}&name=${chatbot.title}`
                )
              }
              onReviewApprovals={() =>
                navigate(`/dashboard/${chatbot.org_id}/workflows/${chatbot.id}/tasks`)
              }
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <EditMethodDialog
        isOpen={editMethodDialog.isOpen}
        onClose={editMethodDialog.onClose}
        orgId={chatbot.org_id}
        chatbotId={chatbot.id}
      />

      <ConfirmDialog
        isOpen={statusDialog.isOpen}
        onClose={statusDialog.onClose}
        onConfirm={handleToggleStatus}
        title={chatbot.blocked ? 'Activate Workflow' : 'Deactivate Workflow'}
        message={
          chatbot.blocked
            ? `Activate "${chatbot.title}"? It will start accepting WhatsApp requests again immediately.`
            : `Deactivate "${chatbot.title}"? It stops accepting WhatsApp requests immediately — anyone who sends ${startCommand(chatbot.short_code)} will no longer be able to start it.`
        }
        confirmText={chatbot.blocked ? 'Activate' : 'Deactivate'}
        cancelText="Cancel"
        isLoading={statusMutation.isPending}
        colorScheme={chatbot.blocked ? 'brand' : 'red'}
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        onConfirm={handleDelete}
        title="Delete Workflow"
        message={`Are you sure you want to delete "${chatbot.title}"? This action cannot be undone.`}
        confirmText="Confirm"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
        colorScheme="red"
      />

      <ConfirmDialog
        isOpen={deleteFlowDialog.isOpen}
        onClose={deleteFlowDialog.onClose}
        onConfirm={handleDeleteFlow}
        title={chatbot.flow_published ? 'Deprecate Flow' : 'Delete Flow'}
        message={
          chatbot.flow_published
            ? `"${chatbot.title}"'s WhatsApp Flow is published and can't be deleted — this will deprecate it instead, permanently stopping it. This cannot be undone, and a new Flow (a fresh sync) will be needed to replace it.`
            : `Are you sure you want to delete the WhatsApp Flow for "${chatbot.title}"? This deletes the Flow on Meta and clears its synced/published state. This action cannot be undone.`
        }
        confirmText={chatbot.flow_published ? 'Deprecate' : 'Confirm'}
        cancelText="Cancel"
        isLoading={deleteFlowMutation.isPending}
        colorScheme="red"
      />

      <Modal
        isOpen={previewDialog.isOpen}
        onClose={previewDialog.onClose}
        title="Flow JSON Preview"
        size="xl"
        footer={
          <Button
            leftIcon={<MdDownload />}
            colorScheme="brand"
            onClick={() =>
              previewJson && copyToClipboard(previewJson, 'Flow JSON copied to clipboard!')
            }
          >
            Copy JSON
          </Button>
        }
      >
        <Box
          as="pre"
          fontFamily="mono"
          fontSize="sm"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          maxH="60vh"
          overflowY="auto"
          bg="bg-muted"
          p={4}
          borderRadius="md"
        >
          {previewJson}
        </Box>
      </Modal>
    </VStack>
  );
}
